import type { FrameworkReadiness } from '@/api/types/compliance-dashboard.types';

// Slice 2 (BI): approved active exceptions grouped by framework. Compliance exceptions are the
// real "needs attention" signal here — no findings/assessment concept exists in this domain. When
// no framework carries an open exception the panel shows an honest empty state, not a zero bar.
interface ExceptionsByFrameworkPanelProps {
  frameworks: FrameworkReadiness[];
  isLoading: boolean;
}

export function ExceptionsByFrameworkPanel({ frameworks, isLoading }: ExceptionsByFrameworkPanelProps) {
  const withExceptions = frameworks
    .filter((f) => f.open_exceptions_count > 0)
    .sort((a, b) => b.open_exceptions_count - a.open_exceptions_count);

  return (
    <div
      className="flex min-w-0 flex-col gap-[11px] rounded-xl border border-border-subtle bg-bg-surface p-[14px_16px_13px]"
      data-testid="exceptions-by-framework"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-text-primary">Exceptions by framework</span>
      </div>
      {isLoading ? (
        <p className="text-[11.5px] text-text-secondary">Loading…</p>
      ) : withExceptions.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-[6px] p-[26px_18px] text-center">
          <span className="text-[13px] font-semibold text-text-muted">No approved exceptions.</span>
          <span className="max-w-[40ch] text-[11.5px] leading-[1.55] text-[#7A8CA3]">
            Approved exceptions will appear here by framework.
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {withExceptions.map((f) => (
            <div
              className="flex items-center gap-[11px] rounded-lg border border-border-subtle bg-[#0D1626] p-[9px_11px]"
              key={f.framework_id}
              data-testid="exception-row"
            >
              <span className="min-w-0 flex-[1_1_auto] truncate text-[12px] text-text-muted">{f.display_name}</span>
              <span className="text-[11.5px] font-bold tabular-nums text-text-muted">{f.open_exceptions_count}</span>
              <span className="whitespace-nowrap text-[10.5px] font-semibold text-accent-amber">active</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
